/*
 * Iter XX.39 — Gully Autonomous Growth Agent · Founder Console
 *
 * Trust Level 1 — Founder Approval required on every send.
 * Self-contained component mounted inside WebFounderDashboard.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const KILL_COLOR = "#ef4444";

export default function GrowthAgentSection() {
  const [campaigns, setCampaigns] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/growth/campaigns");
        if (!alive) return;
        setCampaigns(data.campaigns || []);
        if (!activeId && (data.campaigns || []).length) {
          setActiveId(data.campaigns[0].campaign_id);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load campaigns");
      }
    })();
    return () => { alive = false; };
  }, [refreshTick]);

  const activeCampaign = useMemo(
    () => (campaigns || []).find((c) => c.campaign_id === activeId) || null,
    [campaigns, activeId],
  );

  async function createCampaign(name, fromEmail) {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/growth/campaigns", {
        name, email_from_address: fromEmail,
      });
      setActiveId(data.campaign.campaign_id);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  }

  return (
    <section data-testid="section-growth-agent" className="space-y-6">
      <div className="mb-2">
        <p className="label-eyebrow">growth agent · trust level 1</p>
        <p className="text-xs text-neutral-500 mt-0.5">
          founder approval required · every send audited · phase 1 · academy outreach
        </p>
      </div>

      {error && (
        <div className="surface-1 p-4 text-red-400 text-sm" data-testid="growth-error">
          {error}
        </div>
      )}

      <CreateCampaignBar onCreate={createCampaign} creating={creating} />

      {campaigns && campaigns.length === 0 && (
        <div className="surface-1 p-6 text-neutral-500 text-sm" data-testid="growth-empty">
          No campaigns yet. Create one above to start seeding academies.
        </div>
      )}

      {campaigns && campaigns.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="growth-campaign-tabs">
          {campaigns.map((c) => (
            <button
              key={c.campaign_id}
              onClick={() => setActiveId(c.campaign_id)}
              className={`px-3 py-1.5 text-xs rounded border ${
                c.campaign_id === activeId
                  ? "bg-neutral-800 text-white border-neutral-700"
                  : "bg-transparent text-neutral-400 border-neutral-800 hover:border-neutral-600"
              }`}
              data-testid={`growth-tab-${c.campaign_id}`}
            >
              {c.name}
              {c.kill_switch && <span className="ml-2 text-red-400">·killed</span>}
              {c.paused && !c.kill_switch && <span className="ml-2 text-amber-400">·paused</span>}
            </button>
          ))}
        </div>
      )}

      {activeCampaign && (
        <CampaignDetail
          campaign={activeCampaign}
          onChanged={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function CreateCampaignBar({ onCreate, creating }) {
  const [name, setName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  return (
    <div className="surface-1 p-4 flex flex-wrap gap-2 items-end" data-testid="growth-create-bar">
      <div className="flex-1 min-w-[200px]">
        <label className="label-eyebrow">campaign name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. India academies · phase 1"
          className="w-full mt-1 bg-transparent border-b border-neutral-700 text-sm py-1 focus:outline-none focus:border-neutral-400"
          data-testid="growth-new-name"
        />
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="label-eyebrow">from email (send-mode only)</label>
        <input
          value={fromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
          placeholder="hello@askgully.com"
          className="w-full mt-1 bg-transparent border-b border-neutral-700 text-sm py-1 focus:outline-none focus:border-neutral-400"
          data-testid="growth-new-from-email"
        />
      </div>
      <button
        onClick={() => name.trim() && onCreate(name.trim(), fromEmail.trim() || null)}
        disabled={creating || !name.trim()}
        className="px-3 py-2 text-xs rounded bg-neutral-100 text-black disabled:opacity-40"
        data-testid="growth-create-btn"
      >
        {creating ? "creating…" : "create campaign"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function CampaignDetail({ campaign, onChanged }) {
  const [details, setDetails] = useState(null);
  const [opps, setOpps] = useState(null);
  const [filter, setFilter] = useState("pending_approval");
  const [busy, setBusy] = useState(false);
  const [importText, setImportText] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [minConf, setMinConf] = useState(0.6);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: d1 }, { data: d2 }] = await Promise.all([
          api.get(`/admin/growth/campaigns/${campaign.campaign_id}`),
          api.get(`/admin/growth/campaigns/${campaign.campaign_id}/opportunities`, {
            params: { status: filter, limit: 500 },
          }),
        ]);
        if (!alive) return;
        setDetails(d1.campaign);
        setOpps(d2.opportunities || []);
      } catch (e) { /* soft-fail */ }
    })();
    return () => { alive = false; };
  }, [campaign.campaign_id, filter, rev]);

  async function patchCampaign(patch) {
    setBusy(true);
    try {
      await api.patch(`/admin/growth/campaigns/${campaign.campaign_id}`, patch);
      onChanged();
      setRev((r) => r + 1);
    } finally { setBusy(false); }
  }

  async function doImport() {
    let payload;
    try { payload = JSON.parse(importText); }
    catch { alert("Invalid JSON"); return; }
    const arr = Array.isArray(payload) ? payload : (payload.opportunities || []);
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/growth/campaigns/${campaign.campaign_id}/import`,
        { opportunities: arr },
      );
      alert(`Imported: ${data.imported} · Duplicates: ${data.duplicates} · Invalid: ${data.invalid}`);
      setImportText("");
      setRev((r) => r + 1);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  }

  async function generateDrafts() {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/growth/campaigns/${campaign.campaign_id}/generate-drafts?limit=25`,
      );
      alert(`Generated ${data.drafted} drafts.`);
      setRev((r) => r + 1);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Generate failed");
    } finally { setBusy(false); }
  }

  async function bulkApprove() {
    if (selected.size === 0) {
      if (!window.confirm("Approve ALL pending drafts above the confidence threshold?")) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/growth/campaigns/${campaign.campaign_id}/bulk-approve`,
        {
          opportunity_ids: selected.size ? Array.from(selected) : null,
          min_confidence: minConf,
        },
      );
      alert(
        `Approved: ${data.approved} · Sent: ${data.sent}\n` +
        `Blocked (low confidence): ${data.blocked_low_confidence}\n` +
        `Blocked (safety gate): ${data.blocked_by_gate}`
      );
      setSelected(new Set());
      setRev((r) => r + 1);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Bulk approve failed");
    } finally { setBusy(false); }
  }

  const stats = details?.stats || {};
  const draftOnly = details?.draft_only_mode;

  return (
    <div className="space-y-4" data-testid={`growth-campaign-${campaign.campaign_id}`}>
      <div className="surface-1 p-4 flex flex-wrap gap-4 items-center" data-testid="growth-stats">
        <StatTile label="leads" v={stats.leads} />
        <StatTile label="pending" v={stats.pending_approval} />
        <StatTile label="approved" v={stats.approved} />
        <StatTile label="sent" v={stats.sent} />
        <StatTile label="opted-out" v={stats.opted_out} />
        <StatTile label="rejected" v={stats.rejected} />
        <StatTile label={`sends/24h · cap ${25}`} v={details?.sends_today ?? "—"} />
        <div className="ml-auto flex gap-2 items-center">
          {draftOnly && (
            <span
              className="text-[10px] px-2 py-0.5 rounded border border-amber-800 text-amber-400"
              data-testid="growth-draft-only-badge"
            >
              draft-only (no email creds)
            </span>
          )}
          <button
            onClick={() => patchCampaign({ paused: !details?.paused })}
            className="text-xs px-2 py-1 rounded border border-neutral-700 hover:border-neutral-400 disabled:opacity-40"
            disabled={busy}
            data-testid="growth-pause-toggle"
          >
            {details?.paused ? "resume" : "pause"}
          </button>
          <button
            onClick={() => {
              if (window.confirm(
                details?.kill_switch
                  ? "Un-set kill switch?"
                  : "Kill this campaign? All sends will be blocked until you re-enable.",
              )) patchCampaign({ kill_switch: !details?.kill_switch });
            }}
            className="text-xs px-2 py-1 rounded border"
            style={{
              borderColor: details?.kill_switch ? KILL_COLOR : "#404040",
              color: details?.kill_switch ? KILL_COLOR : "#a3a3a3",
            }}
            disabled={busy}
            data-testid="growth-kill-toggle"
          >
            {details?.kill_switch ? "kill-switch ON" : "kill switch"}
          </button>
        </div>
      </div>

      {/* Import + Generate row */}
      <div className="surface-1 p-4 space-y-2" data-testid="growth-import-row">
        <label className="label-eyebrow">import seed list (json)</label>
        <textarea
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder='[{"name":"Alpha Academy","city":"Mumbai","country":"India","website":"https://alpha.example","public_email":"info@alpha.example","junior_program_evidence":"U-16 program with 40 kids","research_confidence":0.75,"personalization_evidence":[{"fact":"U-16 program with 40 kids","source_url":"https://alpha.example/u16"}],"source_urls":["https://alpha.example/u16"]}]'
          rows={4}
          className="w-full bg-neutral-950 border border-neutral-800 rounded p-2 text-xs font-mono"
          data-testid="growth-import-textarea"
        />
        <div className="flex gap-2">
          <button
            onClick={doImport}
            disabled={busy || !importText.trim()}
            className="text-xs px-3 py-1.5 rounded bg-neutral-100 text-black disabled:opacity-40"
            data-testid="growth-import-btn"
          >
            import
          </button>
          <button
            onClick={generateDrafts}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-neutral-700 hover:border-neutral-400"
            data-testid="growth-generate-drafts-btn"
          >
            generate drafts (up to 25)
          </button>
        </div>
      </div>

      {/* Filter + bulk controls */}
      <div className="flex flex-wrap gap-2 items-center">
        {["pending_approval", "researched", "approved", "sent",
          "rejected", "opted_out", "suppressed", "send_failed"].map((s) => (
          <button
            key={s}
            onClick={() => { setFilter(s); setSelected(new Set()); }}
            className={`text-xs px-2 py-1 rounded border ${
              s === filter
                ? "bg-neutral-800 text-white border-neutral-600"
                : "border-neutral-800 text-neutral-500 hover:border-neutral-600"
            }`}
            data-testid={`growth-filter-${s}`}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto flex gap-2 items-center">
          <label className="flex items-center gap-1 text-xs text-neutral-400">
            <input
              type="checkbox"
              checked={bulkMode}
              onChange={(e) => { setBulkMode(e.target.checked); setSelected(new Set()); }}
              data-testid="growth-bulk-mode-toggle"
            />
            bulk mode
          </label>
          {bulkMode && filter === "pending_approval" && (
            <>
              <label className="text-xs text-neutral-400 flex items-center gap-1">
                min conf
                <input
                  type="number"
                  min="0" max="1" step="0.05"
                  value={minConf}
                  onChange={(e) => setMinConf(parseFloat(e.target.value) || 0)}
                  className="w-16 bg-transparent border-b border-neutral-700 text-xs px-1"
                  data-testid="growth-min-conf-input"
                />
              </label>
              <button
                onClick={bulkApprove}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded bg-emerald-100 text-emerald-950 disabled:opacity-40"
                data-testid="growth-bulk-approve-btn"
              >
                {selected.size === 0
                  ? "approve-all above threshold"
                  : `bulk approve (${selected.size})`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Opportunity cards */}
      <div className="space-y-3" data-testid="growth-opportunity-list">
        {(opps || []).map((o) => (
          <OpportunityCard
            key={o.opportunity_id}
            opp={o}
            bulkMode={bulkMode && filter === "pending_approval"}
            selected={selected.has(o.opportunity_id)}
            onSelect={(v) => {
              setSelected((s) => {
                const n = new Set(s);
                if (v) n.add(o.opportunity_id);
                else n.delete(o.opportunity_id);
                return n;
              });
            }}
            onChanged={() => { setRev((r) => r + 1); onChanged(); }}
            campaign={details || campaign}
          />
        ))}
        {opps && opps.length === 0 && (
          <div className="text-xs text-neutral-500 p-4">
            No opportunities with status <span className="text-neutral-300">{filter}</span>.
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, v }) {
  return (
    <div className="flex flex-col" data-testid={`growth-stat-${label.replace(/\s+/g, "-")}`}>
      <span className="label-eyebrow">{label}</span>
      <span className="font-display text-xl font-light tabular-nums">{v ?? "—"}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OpportunityCard({ opp, bulkMode, selected, onSelect, onChanged, campaign }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(opp?.draft?.subject || "");
  const [body, setBody] = useState(opp?.draft?.body || "");
  const [busy, setBusy] = useState(false);

  const draft = opp.draft || {};
  const conf = typeof draft.confidence === "number" ? draft.confidence : null;
  const belowThreshold = conf !== null && conf < 0.6;

  async function saveEdit() {
    setBusy(true);
    try {
      await api.patch(`/admin/growth/opportunities/${opp.opportunity_id}/draft`,
        { subject, body });
      setEditing(false);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  async function approve() {
    if (!window.confirm(
      campaign?.draft_only_mode
        ? "Approve (draft-only mode — no email will be sent)?"
        : `Approve and send to ${opp.public_email}?`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/growth/opportunities/${opp.opportunity_id}/approve`);
      if (data.status === "sent") {
        alert(`Sent · provider=${data.provider} · id=${data.message_id}`);
      } else if (data.status === "approved") {
        alert(`Approved (${data.reason || "held"})`);
      } else if (data.blocked_reason) {
        alert(`Blocked: ${data.blocked_reason}`);
      } else {
        alert(`Status: ${data.status}`);
      }
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  }

  async function reject() {
    if (!window.confirm("Reject this opportunity?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/growth/opportunities/${opp.opportunity_id}/reject`);
      onChanged();
    } finally { setBusy(false); }
  }

  async function optOut() {
    if (!window.confirm("Mark as opted-out (also suppresses any other opp sharing this email)?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/growth/opportunities/${opp.opportunity_id}/opt-out`);
      onChanged();
    } finally { setBusy(false); }
  }

  async function convertToTeam() {
    const entityType = window.prompt(
      "Convert to team as: academy / club / school / university / rep_team / state / national",
      "academy",
    );
    if (!entityType) return;
    if (!window.confirm(
      `Create Team "${opp.name}" (${entityType}) and email the Coach Welcome Email to ${opp.public_email}?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/teams/from-opportunity", {
        opportunity_id: opp.opportunity_id,
        entity_type: entityType,
      });
      const emailBit = data.email?.accepted
        ? `Coach Welcome Email sent (${data.email.outbound_id})`
        : `Email held: ${data.email?.reason || "draft_only"}`;
      alert(
        `Team created: ${data.team.name}\n` +
        `Invite: ${data.team.invite_url}\n` +
        `Code: ${data.team.invite_code}\n\n${emailBit}`
      );
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Convert failed");
    } finally { setBusy(false); }
  }

  return (
    <div
      className="surface-1 p-4 space-y-3"
      data-testid={`growth-opp-card-${opp.opportunity_id}`}
    >
      <div className="flex gap-3 items-start">
        {bulkMode && (
          <input
            type="checkbox"
            checked={selected}
            disabled={belowThreshold}
            onChange={(e) => onSelect(e.target.checked)}
            title={belowThreshold ? "Below confidence threshold — cannot bulk-approve" : ""}
            data-testid={`growth-opp-select-${opp.opportunity_id}`}
          />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-neutral-100">{opp.name}</span>
            <span className="text-xs text-neutral-500">{[opp.city, opp.country].filter(Boolean).join(", ")}</span>
            <span className="text-xs px-1.5 py-0.5 rounded border border-neutral-800 text-neutral-400">
              {opp.status}
            </span>
            {opp.opted_out && (
              <span className="text-xs px-1.5 py-0.5 rounded border border-red-800 text-red-400">
                opted-out
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-neutral-500">
            {opp.public_email && <span>📧 {opp.public_email}</span>}
            {opp.website && <span>🌐 {opp.website}</span>}
            {opp.instagram && <span>@ {opp.instagram}</span>}
            {opp.decision_maker_name && <span>👤 {opp.decision_maker_name}</span>}
          </div>
        </div>
      </div>

      {/* Facts + confidence */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="label-eyebrow mb-1">research</div>
          <div className="text-neutral-400">
            confidence: <span className={opp.research_confidence >= 0.6 ? "text-emerald-400" : "text-amber-400"}>
              {opp.research_confidence != null ? opp.research_confidence.toFixed(2) : "—"}
            </span>
          </div>
          {opp.junior_program_evidence && (
            <div className="text-neutral-500 mt-1">🏏 {opp.junior_program_evidence}</div>
          )}
          {(opp.source_urls || []).length > 0 && (
            <div className="text-neutral-600 mt-1 truncate">
              sources: {opp.source_urls.slice(0, 3).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="underline mr-2 truncate">
                  {u.length > 40 ? u.slice(0, 40) + "…" : u}
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="label-eyebrow mb-1">personalisation</div>
          {conf !== null ? (
            <div className="text-neutral-400">
              draft confidence:{" "}
              <span className={belowThreshold ? "text-amber-400" : "text-emerald-400"}>
                {conf.toFixed(2)}
              </span>
              {draft.fallback_used && (
                <span className="ml-2 text-neutral-500">(fallback used)</span>
              )}
            </div>
          ) : (
            <div className="text-neutral-500">no draft yet</div>
          )}
          {draft.personalization_fact && (
            <div className="text-neutral-500 mt-1">
              fact: “{draft.personalization_fact}”
              {draft.personalization_source_url && (
                <a href={draft.personalization_source_url} target="_blank" rel="noreferrer" className="underline ml-1">
                  (source)
                </a>
              )}
            </div>
          )}
          {draft.grounding_warning && (
            <div className="text-red-400 mt-1">⚠ grounding: {draft.grounding_warning}</div>
          )}
        </div>
      </div>

      {/* Draft body */}
      {(draft.subject || draft.body || editing) && (
        <div className="border border-neutral-800 rounded p-3 space-y-2">
          {editing ? (
            <>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-transparent border-b border-neutral-700 text-sm py-1"
                placeholder="Subject"
                data-testid={`growth-edit-subject-${opp.opportunity_id}`}
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                className="w-full bg-neutral-950 border border-neutral-800 rounded p-2 text-sm font-mono"
                data-testid={`growth-edit-body-${opp.opportunity_id}`}
              />
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  disabled={busy}
                  className="text-xs px-3 py-1 rounded bg-neutral-100 text-black disabled:opacity-40"
                  data-testid={`growth-edit-save-${opp.opportunity_id}`}
                >
                  save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="text-xs px-3 py-1 rounded border border-neutral-700"
                >
                  cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-neutral-500">subject</div>
              <div className="text-sm text-neutral-200">{draft.subject || "—"}</div>
              <div className="text-xs text-neutral-500 mt-2">body</div>
              <pre className="text-sm text-neutral-200 whitespace-pre-wrap font-sans">{draft.body || "—"}</pre>
              <button
                onClick={() => setEditing(true)}
                className="text-xs underline text-neutral-500 hover:text-neutral-200"
                data-testid={`growth-edit-btn-${opp.opportunity_id}`}
              >
                edit draft
              </button>
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {["pending_approval", "researched"].includes(opp.status) && (
          <button
            onClick={approve}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded bg-emerald-100 text-emerald-950 disabled:opacity-40"
            data-testid={`growth-approve-${opp.opportunity_id}`}
          >
            approve {campaign?.draft_only_mode ? "(hold)" : "& send"}
          </button>
        )}
        {opp.status !== "rejected" && opp.status !== "sent" && opp.status !== "opted_out" && (
          <button
            onClick={reject}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-neutral-700 hover:border-neutral-500"
            data-testid={`growth-reject-${opp.opportunity_id}`}
          >
            reject
          </button>
        )}
        {!opp.opted_out && (
          <button
            onClick={optOut}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-red-900 text-red-400 hover:border-red-500"
            data-testid={`growth-optout-${opp.opportunity_id}`}
          >
            mark opted-out
          </button>
        )}
        {opp.public_email && !opp.team_id && (
          <button
            onClick={convertToTeam}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-emerald-900 text-emerald-400 hover:border-emerald-500 ml-auto"
            data-testid={`growth-convert-team-${opp.opportunity_id}`}
          >
            → convert to team
          </button>
        )}
        {opp.team_id && (
          <span className="text-xs px-2 py-1 rounded border border-emerald-900 text-emerald-400 ml-auto"
                data-testid={`growth-team-badge-${opp.opportunity_id}`}>
            team created
          </span>
        )}
      </div>
    </div>
  );
}
